import React, { useState } from "react";

import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import CheckCircleRoundedIcon from "@material-ui/icons/CheckCircleRounded";
import CloudUploadRoundedIcon from "@material-ui/icons/CloudUploadRounded";

import { useSetup } from "../hooks/useSetup";
import api from "../../../services/api";

import {
  KoraxOnboardingShell,
  KoraxOnboardingContent,
  KoraxOnboardingFooter
} from "../../../components/KoraxUI/Onboarding";

const tones = [
  {
    id: "humanizado",
    title: "Humanizada",
    description: "Natural, próxima e acolhedora."
  },
  {
    id: "consultivo",
    title: "Consultiva",
    description: "Faz perguntas antes de oferecer soluções."
  },
  {
    id: "objetivo",
    title: "Objetiva",
    description: "Respostas rápidas, claras e diretas."
  }
];

const useStyles = makeStyles(theme => ({
  layout: {
    width: "100%",
    maxWidth: 900,
    display: "grid",
    gridTemplateColumns: "1.2fr .8fr",
    gap: 18,

    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr"
    }
  },

  panel: {
    padding: 22,
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.025)"
        : "#FFFFFF"
  },

  title: {
    fontSize: 16,
    fontWeight: 800,
    color: theme.palette.text.primary
  },

  subtitle: {
    marginTop: 6,
    marginBottom: 18,
    fontSize: 12.5,
    lineHeight: 1.5,
    color: theme.palette.text.secondary
  },

  field: {
    marginBottom: 16
  },

  input: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 12
    }
  },

  tone: {
    width: "100%",
    marginBottom: 10,
    padding: 14,
    textAlign: "left",
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    background: "transparent",
    cursor: "pointer"
  },

  toneSelected: {
    borderColor: "#155EEF",
    background: "rgba(21,94,239,.08)"
  },

  upload: {
    marginTop: 18,
    padding: 18,
    borderRadius: 14,
    border: "1px dashed rgba(21,94,239,.35)",
    textAlign: "center"
  },

  uploadIcon: {
    fontSize: 34,
    color: "#155EEF",
    marginBottom: 8
  },

  info: {
    marginTop: 18,
    padding: 14,
    display: "flex",
    gap: 10,
    borderRadius: 12,
    background: "rgba(21,94,239,.06)"
  },

  infoIcon: {
    color: "#16A34A"
  }
}));

export default function TrainingStep() {
  const classes = useStyles();

  const {
    company,
    ai,
    updateAI,
    nextStep,
    previousStep
  } = useSetup();

  const [businessDescription, setBusinessDescription] = useState(
    ai?.businessDescription || ""
  );

  const [mainProducts, setMainProducts] = useState(
    ai?.mainProducts || ""
  );

  const [importantInformation, setImportantInformation] = useState(
    ai?.importantInformation || ""
  );

  const [agentDisplayName, setAgentDisplayName] = useState(
    ai?.agentDisplayName || ""
  );

  const [openingMessage, setOpeningMessage] = useState(
    ai?.openingMessage || ""
  );

  const [salesProcess, setSalesProcess] = useState(
    ai?.salesProcess || ""
  );

  const [closingMethod, setClosingMethod] = useState(
    ai?.closingMethod || ""
  );

  const [serviceArea, setServiceArea] = useState(
    ai?.serviceArea || ""
  );

  const [salesDifferential, setSalesDifferential] = useState(
    ai?.salesDifferential || ""
  );

  const [tone, setTone] = useState(
    ai?.tone || "humanizado"
  );

  const [isPreparing, setIsPreparing] = useState(false);

  const isSalesFlow = ai?.objective === "vendas";

  const canContinue =
    agentDisplayName.trim().length >= 2 &&
    openingMessage.trim().length >= 5 &&
    businessDescription.trim().length >= 10 &&
    mainProducts.trim().length >= 5 &&
    (!isSalesFlow || salesProcess.trim().length >= 5);

  const handleContinue = async () => {
    if (!canContinue || isPreparing) {
      return;
    }

    const trainingData = {
      agentDisplayName: agentDisplayName.trim(),
      openingMessage: openingMessage.trim(),
      businessDescription: businessDescription.trim(),
      mainProducts: mainProducts.trim(),
      importantInformation: importantInformation.trim(),
      salesProcess: salesProcess.trim(),
      closingMethod: closingMethod.trim(),
      serviceArea: serviceArea.trim(),
      salesDifferential: salesDifferential.trim(),
      tone
    };

    setIsPreparing(true);

    try {
      const { data } = await api.post("/onboarding/generate-agent", {
        promptId: Number(ai?.promptId || 0) || undefined,
        companyName:
          company?.name ||
          company?.companyName ||
          "Empresa",
        companySegment: company?.segment || "",
        objective: ai?.objective || "atendimento",
        paymentMethods: ai?.paymentMethods || [],
        pixKeyType: ai?.pixKeyType || "",
        pixKey: ai?.pixKey || "",
        pixReceiverName: ai?.pixReceiverName || "",
        ...trainingData
      });

      const createdPromptId = Number(
        data?.promptId ||
        data?.id ||
        0
      );

      if (!createdPromptId) {
        throw new Error(
          "A API não retornou o identificador do agente."
        );
      }

      updateAI({
        ...trainingData,
        trained: true,
        promptId: createdPromptId
      });

      nextStep();
    } catch (error) {
      console.error(
        "[ONBOARDING] erro ao preparar agente no treinamento",
        error
      );

      alert(
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "Não foi possível preparar sua IA. Tente novamente."
      );
    } finally {
      setIsPreparing(false);
    }
  };

  return (
    <KoraxOnboardingShell step={5} totalSteps={7}>
      <KoraxOnboardingContent
        eyebrow={isSalesFlow ? "Estrutura comercial" : "Treinamento inicial"}
        title={
          isSalesFlow
            ? "Vamos conhecer sua operação comercial"
            : "Ensine sua IA sobre o seu negócio"
        }
        description={
          isSalesFlow
            ? "Essas informações ajudam a IA a qualificar oportunidades e conduzir clientes até o próximo passo da venda."
            : "Essas informações serão usadas como base para preparar o primeiro comportamento da sua IA."
        }
      >
        <div className={classes.layout}>
          <div className={classes.panel}>
            <Typography className={classes.title}>
              Informações da empresa
            </Typography>

            <Typography className={classes.subtitle}>
              Quanto melhor a descrição, melhor será o comportamento inicial da IA.
            </Typography>

            <TextField
              className={`${classes.field} ${classes.input}`}
              variant="outlined"
              fullWidth
              label="Nome do assistente"
              placeholder="Ex.: João"
              value={agentDisplayName}
              onChange={e => {
                const value = e.target.value;
                setAgentDisplayName(value);

                if (!openingMessage.trim()) {
                  const companyName =
                    company?.name ||
                    company?.companyName ||
                    "nossa equipe";

                  if (value.trim()) {
                    setOpeningMessage(
                      `Oi! Aqui é o ${value.trim()}, assistente da ${companyName}. Tudo bem?`
                    );
                  }
                }
              }}
              helperText="Esse é o nome que o cliente verá durante a conversa."
            />

            <TextField
              className={`${classes.field} ${classes.input}`}
              variant="outlined"
              fullWidth
              multiline
              rows={2}
              label="Como seu assistente deve se apresentar?"
              placeholder="Ex.: Oi! Aqui é o João, assistente do Diego. Tudo bem?"
              value={openingMessage}
              onChange={e => setOpeningMessage(e.target.value)}
              helperText="Você pode deixar a apresentação do jeito que combina com seu negócio."
            />

            <TextField
              className={`${classes.field} ${classes.input}`}
              variant="outlined"
              fullWidth
              multiline
              rows={5}
              label="Descreva sua empresa"
              value={businessDescription}
              onChange={e => setBusinessDescription(e.target.value)}
            />

            <TextField
              className={`${classes.field} ${classes.input}`}
              variant="outlined"
              fullWidth
              multiline
              rows={5}
              label="Principais produtos ou serviços"
              value={mainProducts}
              onChange={e => setMainProducts(e.target.value)}
            />

            <TextField
              className={classes.input}
              variant="outlined"
              fullWidth
              multiline
              rows={4}
              label="Informações importantes para seus clientes"
              placeholder="Ex.: horário de atendimento, endereço, formas de pagamento, entregas, garantias ou regras importantes."
              value={importantInformation}
              onChange={e => setImportantInformation(e.target.value)}
            />



            {isSalesFlow && (
              <>
                <Typography className={classes.title} style={{ marginTop: 24 }}>
                  Operação comercial
                </Typography>

                <Typography className={classes.subtitle}>
                  Explique como sua empresa conduz uma oportunidade até a venda.
                </Typography>

                <TextField
                  className={`${classes.field} ${classes.input}`}
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={4}
                  required
                  label="Como funciona seu processo de vendas?"
                  placeholder="Ex.: o cliente solicita informações, recebe um orçamento e depois fala com um consultor."
                  value={salesProcess}
                  onChange={e => setSalesProcess(e.target.value)}
                />

                <TextField
                  className={`${classes.field} ${classes.input}`}
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={3}
                  label="Como a venda normalmente é fechada?"
                  placeholder="Ex.: pagamento pelo WhatsApp, reunião, orçamento aprovado ou atendimento presencial."
                  value={closingMethod}
                  onChange={e => setClosingMethod(e.target.value)}
                />

                <TextField
                  className={`${classes.field} ${classes.input}`}
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={3}
                  label="Onde sua empresa atende ou entrega?"
                  placeholder="Ex.: Guarulhos e região, todo o Brasil ou somente atendimento presencial."
                  value={serviceArea}
                  onChange={e => setServiceArea(e.target.value)}
                />

                <TextField
                  className={classes.input}
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={3}
                  label="Qual é o principal diferencial comercial?"
                  placeholder="Ex.: rapidez, personalização, garantia, preço, qualidade ou atendimento especializado."
                  value={salesDifferential}
                  onChange={e => setSalesDifferential(e.target.value)}
                />
              </>
            )}
          </div>

          <div className={classes.panel}>
            <Typography className={classes.title}>
              Personalidade da IA
            </Typography>

            <Typography className={classes.subtitle}>
              Escolha o estilo predominante da conversa.
            </Typography>

            {tones.map(item => (
              <Button
                key={item.id}
                fullWidth
                className={`${classes.tone} ${tone === item.id ? classes.toneSelected : ""}`}
                onClick={() => setTone(item.id)}
              >
                <div style={{textAlign:"left",width:"100%"}}>
                  <strong>{item.title}</strong><br/>
                  <small>{item.description}</small>
                </div>
              </Button>
            ))}

            <div className={classes.upload}>
              <CloudUploadRoundedIcon className={classes.uploadIcon}/>
              <Typography variant="body2">
                Em breve será possível enviar PDFs, imagens, áudios e vídeos diretamente nesta etapa.
              </Typography>
            </div>
          </div>
        </div>

        <div className={classes.info}>
          <CheckCircleRoundedIcon className={classes.infoIcon}/>
          <Typography variant="body2">
            Todas essas informações poderão ser editadas posteriormente na Central de Treinamento da Korax.
          </Typography>
        </div>
      </KoraxOnboardingContent>

      <KoraxOnboardingFooter
        onBack={previousStep}
        onNext={handleContinue}
        nextLabel={isPreparing ? "Preparando sua IA..." : "Continuar"}
        disableNext={!canContinue || isPreparing}
      />
    </KoraxOnboardingShell>
  );
}
